import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button, Field, Input, Panel } from "@/components/primitives";
import { totpSecretFrom } from "@/lib/totp";
import { authClient, signOut, twoFactor } from "@/lib/auth-client";
import { LOGIN_PATH } from "@/lib/api";

/**
 * First-time setup (decision 48, design §5).
 *
 * Two things must happen before any page opens: the temporary password the
 * owner handed over is replaced, and TOTP is enrolled. `adminAuth` refuses
 * every business route until both are done, so this screen is not skippable
 * — which is what "2FA is genuinely required" means in practice.
 *
 * The backup codes are shown once, here. They are stored hashed and cannot be
 * shown again; losing them means asking the owner to clear the factor.
 */

type Stage = "password" | "enrol" | "confirm" | "codes";

export function SetupPage() {
  const [stage, setStage] = useState<Stage>("password");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [totpUri, setTotpUri] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The QR encodes the whole otpauth:// URI; the "setup key" field in an
  // authenticator takes the base32 secret alone and rejects the URI.
  const secret = totpSecretFrom(totpUri);

  async function changePassword(event: React.FormEvent) {
    event.preventDefault();
    if (newPassword.length < 12) {
      return setError("Use at least 12 characters. A short phrase is fine and easier to recall.");
    }
    setBusy(true);
    setError(null);
    const result = await authClient.changePassword({
      currentPassword,
      newPassword,
      // Everything else this person is signed in on stops working. They set
      // this password seconds ago, so there is nothing to lose and one less
      // live session that knew the temporary one.
      revokeOtherSessions: true,
    });
    setBusy(false);
    if (result.error) {
      // Only a wrong password may say so. A reset ends every session, so a
      // setup tab left open across one gets 401 — and blaming the password
      // there sent people retyping one that was right (2026-10-08).
      if (result.error.status === 401) {
        return setError("Your sign-in has ended. Sign in again with your temporary password.");
      }
      if (result.error.code === "INVALID_PASSWORD") {
        return setError("That current password is not right. It is the one you were handed.");
      }
      return setError("Could not save your password. Try again.");
    }
    setStage("enrol");
  }

  async function startEnrolment() {
    setBusy(true);
    setError(null);
    // Better Auth wants the password again before handing out a TOTP secret.
    const result = await twoFactor.enable({ password: newPassword });
    setBusy(false);
    if (result.error || !result.data) return setError("Could not start enrolment. Try again.");

    // The response is a union: only the TOTP arm carries a secret to scan.
    // We never ask for the email-OTP method, so the other arm means something
    // changed in Better Auth rather than something the person did.
    const enrolment = result.data;
    if (!("totpURI" in enrolment)) {
      return setError("Could not start enrolment. Tell the owner — this needs a look.");
    }
    setTotpUri(enrolment.totpURI);
    setBackupCodes(enrolment.backupCodes ?? []);
    setStage("confirm");
  }

  async function confirmCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await twoFactor.verifyTotp({ code });
    setBusy(false);
    if (result.error) return setError("That code is not right. Codes change every 30 seconds.");
    setStage("codes");
  }

  return (
    <main className="mx-auto grid min-h-dvh max-w-md place-items-center px-4 py-10">
      <div className="w-full">
        <h1 className="mb-6 text-center text-xl font-semibold">Finish setting up</h1>

        {stage === "password" && (
          <Panel
            title="Choose your password"
            description="The one you were handed is temporary and stops working now."
          >
            <form className="grid gap-4 p-5" onSubmit={changePassword}>
              <Field label="Temporary password">
                <Input
                  type="password"
                  autoComplete="current-password"
                  required
                  autoFocus
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </Field>
              <Field label="New password" hint="At least 12 characters.">
                <Input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </Field>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={busy}>
                {busy ? "Saving…" : "Continue"}
              </Button>
            </form>
          </Panel>
        )}

        {stage === "enrol" && (
          <Panel
            title="Add two-factor"
            description="Everyone here uses it — the panel does not open without it."
          >
            <div className="grid gap-4 p-5">
              <p className="text-sm text-muted-foreground">
                You will need an authenticator app: Google Authenticator, 1Password, Authy or any
                other.
              </p>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="button" onClick={startEnrolment} disabled={busy}>
                {busy ? "Preparing…" : "Show my setup key"}
              </Button>
            </div>
          </Panel>
        )}

        {stage === "confirm" && (
          <Panel title="Scan, then confirm">
            <form className="grid gap-4 p-5" onSubmit={confirmCode}>
              <Field label="Scan this" group hint="Google Authenticator, 1Password, Authy — any.">
                <div className="grid place-items-center rounded-md border border-border bg-white p-4">
                  {/* The QR carries the whole otpauth:// URI. Fixed white
                      background: a dark theme would invert it and cameras
                      read an inverted code far less reliably. */}
                  <QRCodeSVG value={totpUri} size={180} />
                </div>
              </Field>
              {secret && (
                <Field
                  label="Or type this key by hand"
                  group
                  hint="Only if you cannot scan. This is the setup key — not a web address."
                >
                  <code className="block rounded-md border border-border bg-secondary p-3 text-center font-mono text-sm tracking-widest break-all">
                    {secret}
                  </code>
                </Field>
              )}
              <Field label="Six-digit code">
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.trim())}
                />
              </Field>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={busy}>
                {busy ? "Checking…" : "Confirm"}
              </Button>
            </form>
          </Panel>
        )}

        {stage === "codes" && (
          <Panel
            title="Save your backup codes"
            description="Shown once. They are the way back in if you lose your phone."
          >
            <div className="grid gap-4 p-5">
              <ul className="grid grid-cols-2 gap-2">
                {backupCodes.map((backupCode) => (
                  <li
                    key={backupCode}
                    className="rounded-md border border-border bg-secondary p-2 text-center font-mono text-sm"
                  >
                    {backupCode}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                Each one works once. If you lose both your phone and these codes, the owner can
                clear your two-factor and you start again.
              </p>
              <Button type="button" onClick={() => window.location.replace("/")}>
                Open the panel
              </Button>
              <Button
                type="button"
                variant="quiet"
                onClick={async () => {
                  await signOut();
                  window.location.replace(LOGIN_PATH);
                }}
              >
                Sign out instead
              </Button>
            </div>
          </Panel>
        )}
      </div>
    </main>
  );
}
